/* =============================================================================
   LICUIWEI Tray Finder - browser runtime
   assets/lcw-tray-finder.js

   This is the theme-side port of finder/tray-match.js from
   outputs/licuiwei-canonical-rebuild, whose behaviour is locked by a 26-case
   test file. The matching rule is unchanged and is the ONLY rule:

       match = (requested capsule size is in the product's supported set)
               AND (the product's hole-count band === the requested capacity)

   There is no title matching, no handle parsing, no fuzzy fallback and no
   hardcoded product list anywhere in this file. Every product comes from the
   data island the section builds out of snippets/lcw-catalog.liquid, which is a
   generated PIPE TABLE (handle|holes|sizes|band|rating|reviews) read with
   `split` - Liquid has no json parser, so parse_json is never involved.
   Also: a "no exact match" answer is a valid, intentional answer.

   All user-visible strings come from a config island rendered from the section
   settings, so wording can be changed in the theme editor without touching code.

   The module also exports its pure parts (capacityBand / matchProducts /
   describeResult) so tools/verify-landing.js can re-run the 24-combination
   matrix against the exact bytes that ship.
   ============================================================================= */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && root) {
    root.LCWFinder = api;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', api.autoInit);
    } else {
      api.autoInit();
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  /* ---- vocabulary fixed by the spec -------------------------------------- */
  var SIZE_EXPAND = { '3-5': ['3', '4', '5'] };
  var CAPACITIES = ['up_to_25', 'around_60', '100_plus'];

  var DEFAULTS = {
    bandLabels: {
      up_to_25: 'up to 25',
      around_60: 'around 60',
      '100_plus': '100 or more',
    },
    idleTitle: 'Pick a capsule size to begin',
    idleBody:
      'Answer the two questions and we will show you the exact tray. Nothing to sign up for.',
    unsureTitle: 'No problem - start with size 0.',
    unsureBody:
      'Size 0 fits most supplement powders and is the most common starting point. Measure a capsule you already have against our capsule size guide, then pick your batch size.',
    noMatchTitle: 'No exact match found.',
    noMatchBody:
      'Nothing in our range fits size {size} capsules at {capacity} per batch. We would rather say so than send you the wrong tray.',
    countOne: '1 exact match for size {size} &middot; {capacity} capsules',
    countMany: '{n} exact matches for size {size} &middot; {capacity} capsules',
    why: 'Takes size {size} capsules and handles {capacity} per batch. {holes} holes per tray.',
    viewOne: 'View this tray',
    viewMany: 'View',
    browseLabel: 'Browse all capsule fillers',
    browseUrl: '/collections/best-capsule-filler-tray',
    guideLabel: 'Open the capsule size guide',
    guideUrl: '',
    holesSuffix: 'holes',
    sizePrefix: 'Size ',
    noReviews: 'No reviews yet',
    verified: '',
  };

  /* ---- pure logic -------------------------------------------------------- */

  /* Band boundaries, identical to the verified core. Anything outside the three
   * bands is reported as UNBANDED / UNKNOWN rather than guessed. */
  function capacityBand(holeCount) {
    if (holeCount === null || holeCount === undefined || isNaN(holeCount)) return 'UNKNOWN';
    if (holeCount <= 25) return 'up_to_25';
    if (holeCount === 60) return 'around_60';
    if (holeCount >= 100) return '100_plus';
    return 'UNBANDED';
  }

  /* Exact match only. Deterministic order: holes, then price, then handle. */
  function matchProducts(products, size, capacity) {
    var want = SIZE_EXPAND[size] || [String(size)];
    return products
      .filter(function (p) {
        if (!p) return false;
        var sizes = p.sizes || [];
        var band = p.band || capacityBand(p.hole_count);
        var sizeOk = want.some(function (s) {
          return sizes.indexOf(s) !== -1;
        });
        var bandKnown = band !== 'UNKNOWN' && band !== 'UNBANDED';
        return sizeOk && band === capacity && bandKnown;
      })
      .sort(function (a, b) {
        return (
          a.hole_count - b.hole_count ||
          a.price_min - b.price_min ||
          String(a.handle).localeCompare(String(b.handle))
        );
      });
  }

  function cfg(config, key) {
    if (config && typeof config[key] === 'string' && config[key] !== '') return config[key];
    return DEFAULTS[key] === undefined ? '' : DEFAULTS[key];
  }

  function bandLabel(config, capacity) {
    var m = (config && config.bandLabels) || DEFAULTS.bandLabels;
    return m[capacity] || capacity;
  }

  function sizeLabel(size) {
    return size === '3-5' ? '3&ndash;5' : String(size);
  }

  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (all, k) {
      return vars[k] === undefined ? all : vars[k];
    });
  }

  /* Shopify hands Liquid `price_min` as an INTEGER in the currency's smallest
   * unit - 19.99 arrives as 1999 - so the raw number must never be printed
   * with toFixed(2) on its own (that is how "$1999.00" appeared on a $19.99
   * tray).  The section therefore also ships `price_html`, already formatted
   * by Shopify's own `money` filter, which follows the store currency, symbol
   * and decimal separator.  The raw value stays because it is what the price
   * sort compares; dividing by hand is only the fallback for an older island
   * that carries no price_html. */
  function money(n, html) {
    if (html) return String(html);
    var v = Number(n);
    if (isNaN(v)) return '';
    return '$' + (v / 100).toFixed(2);
  }

  function stars(n) {
    var f = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
    return '\u2605'.repeat(f) + '\u2606'.repeat(5 - f);
  }

  function esc(s) {
    return String(s === undefined || s === null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function imageTag(p, cls) {
    if (!p.image) return '';
    return '<img class="' + (cls || '') + '" src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy">';
  }

  function rateLine(p, config) {
    if (!p.rating) {
      return '<div class="lcw-rate lcw-rate--empty">' + esc(cfg(config, 'noReviews')) + '</div>';
    }
    return (
      '<div class="lcw-rate"><span>' + stars(p.rating) + '</span> ' +
      Number(p.rating).toFixed(1) + ' <i>(' + (p.reviews || 0) + ')</i></div>'
    );
  }

  function chips(p, config) {
    return (
      '<div class="lcw-chips"><span><b>' + p.hole_count + '</b> ' + esc(cfg(config, 'holesSuffix')) + '</span>' +
      '<span>' + esc(cfg(config, 'sizePrefix')) + (p.sizes || []).join(' / ') + '</span></div>'
    );
  }

  function linkHtml(url, label, cls) {
    if (!url) return '';
    return '<a class="' + (cls || 'lcw-link--accent') + '" href="' + esc(url) + '">' + esc(label) + ' &rarr;</a>';
  }

  /* Returns the HTML for one result state. Pure function - no DOM access. */
  function describeResult(products, state, config) {
    var size = state.size;
    var capacity = state.capacity;

    if (size === 'unsure') {
      return (
        '<div class="lcw-nomatch"><b>' + esc(cfg(config, 'unsureTitle')) + '</b>' +
        '<p>' + esc(cfg(config, 'unsureBody')) + '</p>' +
        linkHtml(cfg(config, 'guideUrl'), cfg(config, 'guideLabel')) +
        '</div>'
      );
    }

    if (!size || !capacity) {
      return (
        '<div class="lcw-result__nm">' + esc(cfg(config, 'idleTitle')) + '</div>' +
        '<p class="lcw-result__sub">' + esc(cfg(config, 'idleBody')) + '</p>'
      );
    }

    var hits = matchProducts(products, size, capacity);
    var vars = {
      size: sizeLabel(size),
      capacity: bandLabel(config, capacity),
      n: hits.length,
    };

    if (!hits.length) {
      return (
        '<div class="lcw-nomatch"><b>' + esc(cfg(config, 'noMatchTitle')) + '</b>' +
        '<p>' + esc(fill(cfg(config, 'noMatchBody'), vars)) + '</p>' +
        linkHtml(cfg(config, 'browseUrl'), cfg(config, 'browseLabel')) +
        '</div>'
      );
    }

    if (hits.length === 1) {
      var p = hits[0];
      return (
        '<div class="lcw-win">' +
        '<div class="lcw-win__img">' + imageTag(p) + '</div>' +
        rateLine(p, config) +
        '<h3 class="lcw-win__title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h3>' +
        chips(p, config) +
        '<p class="lcw-win__why">' + esc(fill(cfg(config, 'why'), {
          size: sizeLabel(size),
          capacity: bandLabel(config, capacity),
          holes: p.hole_count,
        })) + '</p>' +
        '<div class="lcw-win__ft"><span class="lcw-price">' + money(p.price_min, p.price_html) + '</span>' +
        '<a class="lcw-btn lcw-btn--primary lcw-btn--sm" href="' + esc(p.url) + '">' +
        esc(cfg(config, 'viewOne')) + ' &rarr;</a></div>' +
        '</div>'
      );
    }

    return (
      '<p class="lcw-count">' + esc(fill(cfg(config, 'countMany'), vars)) + '</p>' +
      '<div class="lcw-cards">' +
      hits
        .map(function (p) {
          return (
            '<article class="lcw-card">' +
            '<a class="lcw-card__ph" href="' + esc(p.url) + '">' + imageTag(p) + '</a>' +
            '<div class="lcw-card__bd">' + rateLine(p, config) +
            '<h3 class="lcw-card__title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h3>' +
            chips(p, config) +
            '<div class="lcw-card__ft"><span class="lcw-price">' + money(p.price_min, p.price_html) + '</span>' +
            '<a class="lcw-mini" href="' + esc(p.url) + '">' + esc(cfg(config, 'viewMany')) + ' &rarr;</a></div>' +
            '</div></article>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  /* ---- DOM wiring -------------------------------------------------------- */

  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initOne(el) {
    var id = el.getAttribute('data-section-id') || '';
    var products = readJson('lcw-finder-data-' + id) || [];
    var config = readJson('lcw-finder-config-' + id) || {};
    var out = el.querySelector('[data-lcw-result]');
    if (!out) return;
    var state = { size: null, capacity: null };

    var opts = el.querySelectorAll('[data-lcw-opt]');
    Array.prototype.forEach.call(opts, function (b) {
      b.addEventListener('click', function () {
        var g = b.getAttribute('data-group');
        Array.prototype.forEach.call(el.querySelectorAll('[data-lcw-opt][data-group="' + g + '"]'), function (x) {
          x.classList.remove('is-on');
          x.setAttribute('aria-pressed', 'false');
        });
        b.classList.add('is-on');
        b.setAttribute('aria-pressed', 'true');
        state[g] = b.getAttribute('data-value');
        out.innerHTML = describeResult(products, state, config);
      });
    });

    out.innerHTML = describeResult(products, state, config);
  }

  function autoInit() {
    var nodes = document.querySelectorAll('[data-lcw-finder]');
    Array.prototype.forEach.call(nodes, initOne);
  }

  return {
    SIZE_EXPAND: SIZE_EXPAND,
    CAPACITIES: CAPACITIES,
    capacityBand: capacityBand,
    matchProducts: matchProducts,
    describeResult: describeResult,
    init: initOne,
    autoInit: autoInit,
  };
});
